import { createRouter, createWebHistory } from 'vue-router'

import Login from '@/views/login/Login.vue'
import VideoList from '@/views/video/VideoList.vue'
import PermissionTest from '@/views/permission-test/PermissionTest.vue'
import Forbidden from '@/views/error/Forbidden.vue'
import { hasPermission } from '@/api/auth'

const routes = [
  {
    path: '/',
    redirect: '/videos'
  },
  {
    path: '/login',
    name: 'Login',
    component: Login
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: Forbidden,
    meta: {
      title: '无访问权限'
    }
  },
  {
    path: '/videos',
    name: 'VideoList',
    component: () => import('@/workspace/WorkspaceProjects.vue'),
    meta: {
      permission: 'video:page',
      requiresAuth: true, // 需要登录
      title: '剧本项目'
    }
  },
  {
    path: '/videos/tables',
    name: 'FeishuTables',
    component: () => import('@/views/feishu/FeishuTables.vue'),
    meta: { permission: 'feishu:page', requiresAuth: true, title: '表格', workspaceCache: false }
  },
  {
    path: '/videos/studio/:scriptId',
    name: 'VideoStudio',
    component: VideoList,
    meta: {
      permission: 'video:page',
      requiresAuth: true,
      title: '剧本视频创作'
    }
  },
  {
    path: '/videos/:id',
    name: 'VideoDetail',
    component: () => import('@/views/video/VideoDetail.vue'),
    meta: {
      permission: 'video:page',
      requiresAuth: true, // 需要登录
      title: '视频详情'
    }
  },
  {
    path: '/image-view',
    name: 'ImageView',
    component: () => import('@/views/image-view/ImageView.vue'),
    meta: {
      permission: 'image_view:page',
      requiresAuth: true, // 需要登录
      title: '看图版'
    }
  },
  {
    path: '/voice-service',
    name: 'VoiceService',
    component: () => import('@/views/voice-service/VoiceService.vue'),
    meta: {
      permission: 'voice:page',
      requiresAuth: true, // 需要登录
      title: '配音服务'
    }
  },
  {
    path: '/image-generate',
    name: 'ImageGenerate',
    component: () => import('@/views/image-generate/ImageGenerate.vue'),
    meta: {
      permission: 'image:page',
      requiresAuth: true, // 需要登录
      title: '生图'
    }
  },
  {
    path: '/image-generate/studio/:scriptId',
    name: 'ImageGenerateStudio',
    component: () => import('@/views/image-generate/ImageGenerate.vue'),
    meta: {
      permission: 'image:page',
      requiresAuth: true,
      title: '剧本图片创作'
    }
  },
  {
    path: '/inspiration-material',
    name: 'InspirationMaterial',
    component: () => import('@/views/inspiration-material/InspirationMaterial.vue'),
    meta: {
      permission: ['inspiration:page', 'inspiration:view'],
      requiresAuth: true,
      title: '灵感素材库'
    }
  },
  {
    path: '/music-generate',
    name: 'MusicGenerate',
    component: () => import('@/views/music-generate/MusicGenerate.vue'),
    meta: {
      permission: 'music:page',
      requiresAuth: true,
      title: 'AI音乐'
    }
  },
  {
    path: '/subtitle-removal',
    name: 'SubtitleRemoval',
    component: () => import('@/views/subtitle-removal/SubtitleRemoval.vue'),
    meta: {
      permission: 'subtitle_removal:page',
      requiresAuth: true,
      title: '去字幕'
    }
  },
  {
    path: '/script-asset-generate',
    name: 'ScriptAssetGenerate',
    component: () => import('@/views/script-asset/ScriptAssetGenerate.vue'),
    meta: {
      permission: 'script_asset:page',
      requiresAuth: true,
      title: '剧本资产生图'
    }
  },
  {
    path: '/volc-asset',
    name: 'VolcAsset',
    component: () => import('@/views/volc-asset/VolcAsset.vue'),
    meta: {
      permission: 'volc_asset:page',
      requiresAuth: true,
      title: '虚拟人像素材库'
    }
  },
  {
    path: '/transfer',
    name: 'TransferBoard',
    component: () => import('@/views/transfer-board/TransferBoard.vue'),
    meta: {
      requiresAuth: true,
      title: '公共中转板'
    }
  },
  {
    path: '/canvas',
    name: 'CanvasList',
    component: () => import('@/views/canvas/CanvasListView.vue'),
    meta: {
      permission: 'canvas:page',
      requiresAuth: true,
      title: '画布列表'
    }
  },
  {
    path: '/canvas/smart/:id',
    name: 'SmartCanvas',
    component: () => import('@/views/canvas/SmartCanvasView.vue'),
    meta: {
      permission: 'canvas:page',
      requiresAuth: true,
      title: '智能画布'
    }
  },
  {
    path: '/permission-test',
    name: 'PermissionTest',
    component: PermissionTest,
    meta: {
      requiresAuth: true, // 需要登录
      title: '权限测试',
      permission: 'video:page'
    }
  }
]

routes.splice(routes.length - 1, 0, {
  path: '/script-agent',
  component: () => import('@/views/script-agent/ScriptStudioLayout.vue'),
  meta: { requiresAuth: true, title: '智能剧本' },
  children: [
    {
      path: '',
      name: 'ScriptAgentWorkspace',
      component: () => import('@/views/script-agent/ScriptAgentWorkspace.vue')
    },
    {
      path: '/script-agent/projects/:projectId/episodes/:episodeId/storyboard',
      name: 'EpisodeStoryboard',
      component: () => import('@/views/script-agent/EpisodeStoryboard.vue'),
      meta: { requiresAuth: true, title: '分集分镜' }
    }
  ]
})

routes.splice(routes.length - 1, 0, {
  path: '/quick-script',
  component: () => import('@/views/quick-script/QuickScriptStudioLayout.vue'),
  meta: { requiresAuth: true, title: '快速剧本' },
  children: [
    {
      path: '',
      name: 'QuickScriptWorkspace',
      component: () => import('@/views/quick-script/QuickScriptWorkspace.vue')
    },
    {
      path: '/quick-script/projects/:projectId/episodes/:episodeId/storyboard',
      name: 'QuickScriptEpisodeStoryboard',
      component: () => import('@/views/quick-script/QuickScriptEpisodeStoryboard.vue'),
      meta: { requiresAuth: true, title: '分集分镜' }
    }
  ]
})

// The workbench owns navigation and page caching without changing public URLs.
const workspaceRouteNames = new Set([
  'VideoList', 'FeishuTables', 'VideoStudio', 'VideoDetail', 'ImageGenerate', 'ImageGenerateStudio',
  'InspirationMaterial', 'CanvasList', 'SmartCanvas', 'SubtitleRemoval',
  'MusicGenerate', 'VoiceService', 'VolcAsset'
])
for (const route of routes) {
  if (workspaceRouteNames.has(route.name) || ['/script-agent', '/quick-script'].includes(route.path)) {
    route.meta = { ...route.meta, workspace: true }
    if (route.name === 'VideoDetail') route.meta.workspaceCache = false
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // 页面跳转时的滚动行为
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// 路由守卫：检查是否需要登录
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('token')

  // 1. 如果路由需要认证但用户未登录，跳转到登录页（携带原始目标路径）
  if (to.meta.requiresAuth && !token) {
    next({ path: '/login', query: { redirect: to.fullPath }, replace: true })
    return
  }

  if (to.meta.permission && !hasPermission(to.meta.permission)) {
    next({
      path: '/forbidden',
      query: {
        redirect: to.fullPath
      },
      replace: true
    })
    return
  }

  // 2. 如果已登录用户访问登录页，跳转到首页（或 redirect 目标）
  //    但如果目标页需要权限且用户不具备，则留在登录页（让用户重新登录）
  if (to.path === '/login' && token) {
    const redirect = to.query.redirect
    const target = redirect && redirect !== '/login' ? redirect : '/videos'
    const targetRoute = router.resolve(target)
    if (
      targetRoute.meta?.permission &&
      !hasPermission(targetRoute.meta.permission)
    ) {
      // 权限不足，清除旧凭证让用户重新登录
      localStorage.removeItem('token')
      next()
      return
    }
    next(target)
    return
  }

  // 3. 如果从登录页面跳转到 /videos 页面，延迟刷新一次
  if (from.path === '/login' && to.path === '/videos' && token) {
    // 先正常导航
    next()
    // 延迟 500ms 后刷新页面，确保组件已完全加载
    // setTimeout(() => {
    //   window.location.reload()
    // }, 100)
    return
  }

  // 4. 其他情况正常导航
  next()
})

export default router
